import React, { useEffect, useState, useCallback } from "react";
import { api, Incident } from "../api";

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function agentLabel(agent: string | null): string {
  const map: Record<string, string> = {
    document_qa: "Document QA",
    web_search:  "Web Search",
    general:     "General",
    code_help:   "Code Help",
  };
  return agent ? (map[agent] ?? agent) : "Orchestrator";
}

export default function IncidentsPanel() {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading]     = useState(true);
  const [expanded, setExpanded]   = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setIncidents(await api.getIncidents());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(id: number) {
    await api.deleteIncident(id);
    setIncidents(prev => prev.filter(i => i.id !== id));
  }

  async function handleClearAll() {
    if (!window.confirm("Clear all incidents?")) return;
    await api.clearIncidents();
    setIncidents([]);
  }

  return (
    <div className="incidents-panel">
      <div className="incidents-header">
        <div>
          <h2 className="incidents-title">Failed Queries</h2>
          <p className="incidents-subtitle">
            {incidents.length === 0
              ? "No failures recorded."
              : `${incidents.length} incident${incidents.length !== 1 ? "s" : ""} logged (most recent first)`}
          </p>
        </div>
        <div className="incidents-actions">
          <button className="btn-secondary" onClick={load} disabled={loading}>
            {loading ? "Loading…" : "Refresh"}
          </button>
          {incidents.length > 0 && (
            <button className="btn-danger" onClick={handleClearAll}>
              Clear All
            </button>
          )}
        </div>
      </div>

      {loading && <div className="incidents-empty">Loading…</div>}

      {!loading && incidents.length === 0 && (
        <div className="incidents-empty">
          No failed queries yet. When the AI model times out or returns an error, it will appear here.
        </div>
      )}

      {!loading && incidents.length > 0 && (
        <div className="incidents-list">
          {incidents.map(inc => (
            <div key={inc.id} className="incident-card">
              <div className="incident-row">
                <div className="incident-meta">
                  <span className="incident-time">{formatDate(inc.created_at)}</span>
                  <span className={`incident-badge agent-${inc.agent_used ?? "unknown"}`}>
                    {agentLabel(inc.agent_used)}
                  </span>
                  <span className="incident-error-type">{inc.error_type}</span>
                </div>
                <div className="incident-row-right">
                  <button
                    className="btn-link"
                    onClick={() => setExpanded(expanded === inc.id ? null : inc.id)}
                  >
                    {expanded === inc.id ? "Hide" : "Details"}
                  </button>
                  <button
                    className="btn-link btn-link-danger"
                    onClick={() => handleDelete(inc.id)}
                    title="Dismiss"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="incident-query">"{inc.query}"</div>

              {expanded === inc.id && inc.error_message && (
                <pre className="incident-trace">{inc.error_message}</pre>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
